import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatPrice } from "@/lib/format";
import {
  DEALER_MIN_MONTHLY_CREDITS,
  dealerCreditUnitPrice,
  dealerMonthlyCreditsFromInput,
  dealerMonthlyPriceHuf,
} from "@/lib/dealerPackageCalc";

const fieldCls =
  "w-full bg-anthracite-900 border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:border-amber-400/50 outline-none";

export function DealerPackageCard({ config, user, activeSub }) {
  const navigate = useNavigate();
  const [rotating, setRotating] = useState(10);
  const [spots, setSpots] = useState(10);
  const [manualCredits, setManualCredits] = useState(5000);
  const [loading, setLoading] = useState(false);

  if (!config) return null;

  const unitPrice = dealerCreditUnitPrice(config);
  const monthlyCredits = dealerMonthlyCreditsFromInput(manualCredits);
  const monthlyPrice = monthlyCredits ? dealerMonthlyPriceHuf(config, monthlyCredits) : 0;
  const hasOtherSub = activeSub && !activeSub.dealer_pack;

  const subscribe = async () => {
    if (!user) return navigate("/belepes");
    if (hasOtherSub) {
      toast.error("Előbb mondd le a meglévő kreditcsomag előfizetést.");
      return;
    }
    const r = Math.max(10, Math.floor(Number(rotating) || 0));
    const g = Math.max(10, Math.floor(Number(spots) || 0));
    const credits = dealerMonthlyCreditsFromInput(manualCredits);
    if (r < 10 || g < 10) {
      toast.error("Minimum 10 forgó autó és 10 garázshely.");
      return;
    }
    if (!credits) {
      toast.error(`Adj meg legalább ${DEALER_MIN_MONTHLY_CREDITS.toLocaleString("hu-HU")} havi kreditet.`);
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/credits/subscribe/dealer", {
        rotating_cars: r,
        garage_spots: g,
        monthly_credits: credits,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nem sikerült elindítani az előfizetést.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="dealer-package-card"
      className="glass rounded-3xl p-6 flex flex-col h-full border border-amber-400/25 bg-gradient-to-br from-amber-500/[0.06] to-transparent"
    >
      <span className="self-start px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 text-[10px] tracking-[0.2em] uppercase mb-3">
        Kereskedői
      </span>
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-widest text-slate-400">Havi előfizetés</span>
      </div>
      <div className="font-heading text-4xl font-light" data-testid="dealer-monthly-credits">
        {monthlyCredits != null ? monthlyCredits.toLocaleString("hu-HU") : "—"}
      </div>
      <div className="text-sm text-slate-400 mt-1">kredit / hó · {unitPrice} Ft / kredit</div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] uppercase text-slate-500 block mb-1" htmlFor="dealer-rotating">
            Forgó autó
          </label>
          <input
            id="dealer-rotating"
            data-testid="dealer-rotating-cars"
            type="number"
            min={10}
            value={rotating}
            onChange={(e) => setRotating(e.target.value)}
            className={fieldCls}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 block mb-1" htmlFor="dealer-spots">
            Garázs
          </label>
          <input
            id="dealer-spots"
            data-testid="dealer-garage-spots"
            type="number"
            min={10}
            value={spots}
            onChange={(e) => setSpots(e.target.value)}
            className={fieldCls}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 block mb-1" htmlFor="dealer-manual-credits">
            Havi kredit
          </label>
          <input
            id="dealer-manual-credits"
            data-testid="dealer-manual-credits"
            type="number"
            min={DEALER_MIN_MONTHLY_CREDITS}
            value={manualCredits}
            onChange={(e) => setManualCredits(e.target.value)}
            className={fieldCls}
          />
        </div>
      </div>
      <p className="text-[10px] text-slate-500 mt-1.5">
        Forgó autó és garázs a csomaghoz — havi kredit min. {DEALER_MIN_MONTHLY_CREDITS.toLocaleString("hu-HU")}.
      </p>

      <div className="mt-auto pt-4">
        <div className="font-heading text-2xl" data-testid="dealer-monthly-price">
          {monthlyCredits ? formatPrice(monthlyPrice) : "—"}
          <span className="text-xs text-slate-500"> / hó</span>
        </div>
        <button
          type="button"
          data-testid="dealer-subscribe"
          disabled={loading || !monthlyCredits || (activeSub?.dealer_pack && activeSub?.status === "active")}
          onClick={subscribe}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-anthracite-900 text-sm font-medium transition-all"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {activeSub?.dealer_pack ? "Előfizetés aktív" : "Kereskedői előfizetés"}
        </button>
        {hasOtherSub && (
          <p className="text-[10px] text-amber-300/80 mt-2 text-center">Előbb mondd le a másik előfizetést.</p>
        )}
      </div>
    </div>
  );
}
